/**
 * Two kinds of temporal value arrive from the API, and they must not be formatted
 * the same way.
 *
 * A **plain** value (`2026-07-01`, `2026-07-01T00:00:00`) is a calendar position in
 * the business timezone — a chart bucket, a shift date. It carries no instant.
 * Passing one through `new Date(...).toLocaleDateString()` makes the browser read
 * it as UTC and re-render it in the viewer's zone, which moves it backwards a day
 * (or a month) for everyone west of UTC. Use `formatPlain`.
 *
 * An **instant** (`2026-07-05T22:00:00.000Z`) is a real moment. Business-derived
 * instants render in the *business* timezone so a number means the same thing to
 * an owner in Toronto and a bookkeeper in Vancouver. Use `formatInstant`.
 */

const PLAIN = /^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}(?::\d{2})?))?$/;

/** Reads a plain wall clock as UTC so `timeZone: 'UTC'` renders it back unchanged. */
function plainToUtc(plain: string): Date | null {
  const m = PLAIN.exec(plain);
  if (!m) return null;
  const d = new Date(`${m[1]}T${m[2] ?? '00:00:00'}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Falls back to the viewer's zone rather than throwing on an unrecognised IANA name. */
function withZone(
  opts: Intl.DateTimeFormatOptions,
  timeZone: string | null,
): Intl.DateTimeFormat {
  if (timeZone) {
    try {
      return new Intl.DateTimeFormat(undefined, { ...opts, timeZone });
    } catch {
      /* unknown zone — fall through */
    }
  }
  return new Intl.DateTimeFormat(undefined, opts);
}

/**
 * Formats a plain calendar value exactly as given. The timezone is pinned to UTC
 * internally to defeat conversion — it is not a claim that the value is UTC.
 */
export function formatPlain(
  plain: string | null | undefined,
  opts: Intl.DateTimeFormatOptions,
): string {
  const d = plain ? plainToUtc(plain) : null;
  if (!d) return '—';
  return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: 'UTC' }).format(d);
}

/** Formats a true instant in an explicit zone — pass the business timezone. */
export function formatInstant(
  iso: string | null | undefined,
  timeZone: string | null,
  opts: Intl.DateTimeFormatOptions,
): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return withZone(opts, timeZone).format(d);
}

/** The calendar day an instant falls on in `timeZone`, as `YYYY-MM-DD`. */
export function instantToPlainDate(
  iso: string | null | undefined,
  timeZone: string | null,
): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const parts = withZone(
    { year: 'numeric', month: '2-digit', day: '2-digit' },
    timeZone,
  ).formatToParts(d);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** Short zone name for labelling a surface that renders in business time (e.g. "EDT"). */
export function zoneAbbreviation(timeZone: string | null): string {
  if (!timeZone) return '';
  try {
    return (
      new Intl.DateTimeFormat(undefined, { timeZone, timeZoneName: 'short' })
        .formatToParts(new Date())
        .find((p) => p.type === 'timeZoneName')?.value ?? ''
    );
  } catch {
    return '';
  }
}
